import { useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { Checkbox, Text, Colors } from '@mercator-gcp/grout';
import { Spinner } from 'components/elements/Spinner';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import Header from 'components/widgets/SearchFilterWizard/steps/components/Header';
import SectionWrapper from 'components/widgets/SearchFilterWizard/steps/components/SectionWrapper';
import { usePresetFilters } from 'hooks/filterToolbar/usePresetFilters';
import {
  useDashboardTriggers,
  useDashboardCompanyStakeholderTypesExclusions,
  useDashboardExcludePastProjectStage,
  useDashboardPastProjectStage,
} from 'state/dashboard';
import { State } from 'utils/constants/location';
import {
  getRegionFromSelectedState,
  ROLE_ID_TO_PRESET_KEY,
} from 'utils/constants/presetFilters';
import { groupRawToGroup } from 'utils/transforms';

const checkboxContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'padding_12',
  marginTop: 'padding_16',
  '@media (max-width: 1280px)': {
    gap: 'padding_8',
    marginTop: 'padding_12',
  },
  '@media (max-width: 1024px)': {
    gap: 'padding_8',
    marginTop: 'padding_8',
  },
};

const checkboxItemStyle = (isSelected: boolean, isLoading: boolean) => ({
  padding: 'padding_16',
  borderRadius: '4px',
  border: `1px solid ${isSelected ? Colors.ForestGreen900 : Colors.ArmyGreen200}`,
  backgroundColor: isSelected ? Colors.ArmyGreen100 : Colors.White,
  cursor: isLoading ? 'wait' : 'pointer',
  transition: 'all 0.2s ease',
  width: '100%',
  textAlign: 'left',
  opacity: isLoading && isSelected ? 0.9 : 1,
  _hover: isLoading
    ? {}
    : {
        borderColor: isSelected ? Colors.ForestGreen900 : Colors.ArmyGreen300,
        backgroundColor: isSelected ? Colors.ArmyGreen100 : Colors.ArmyGreen50,
      },
});

const roleTitleStyle = {
  color: Colors.ArmyGreen900,
  marginBottom: 'padding_8',
};

const goalTextStyle = {
  color: Colors.TextQuiet,
};

type RoleType = 'general_contractor' | 'subcontractor' | null;

const ROLES = [
  {
    id: 'general_contractor',
    title: "I'm a General Contractor",
    goalText:
      'Find opportunities at the earliest possible stage, from land acquisition through design, before a GC is selected',
  },
  {
    id: 'subcontractor',
    title: "I'm a Subcontractor / Specialty Trade",
    goalText:
      'Find opportunities where the GC has been selected but the design and planning are still underway.',
  },
];

type LatestProjectStageStepProps = {
  selectedStages: string[];
  selectedState: string | null;
  onStagesChange: (stages: string[]) => void;
};

const LatestProjectStageStep = ({
  selectedStages,
  selectedState,
  onStagesChange,
}: LatestProjectStageStepProps) => {
  const [, saveTriggers] = useDashboardTriggers();
  const [, setStakeholderExclusions] = useDashboardCompanyStakeholderTypesExclusions();
  const [, setPastProjectStages] = useDashboardPastProjectStage();
  const [, setExcludePastProjectStages] = useDashboardExcludePastProjectStage();
  const region = getRegionFromSelectedState(selectedState as State);

  // Determine selected role from selectedStages
  const selectedRole: RoleType = (() => {
    if (selectedStages.includes('general_contractor') || selectedStages.includes('GC')) {
      return 'general_contractor';
    }
    if (
      selectedStages.includes('subcontractor') ||
      selectedStages.includes('Subcontractor')
    ) {
      return 'subcontractor';
    }

    return null;
  })();

  // Use same preset API as filter toolbar "Preset for GCs" / "Preset for Subs" buttons
  const presetKey =
    selectedRole && region ? ROLE_ID_TO_PRESET_KEY[selectedRole] : undefined;

  const { data, isLoading } = usePresetFilters({
    presetFilterName: presetKey,
    region,
  });

  // Apply preset data when loaded (same as PresetFilterMenu), or clear when no role
  useEffect(() => {
    if (!selectedRole) {
      saveTriggers([]);
      setStakeholderExclusions('');
      setPastProjectStages([]);
      setExcludePastProjectStages([]);

      return;
    }

    if (!data?.data || isLoading) return;

    const converted = groupRawToGroup(data.data as any);
    saveTriggers(converted.triggers || []);
    setPastProjectStages(converted.past_project_stage || []);
    setExcludePastProjectStages(converted.exclude_past_project_stage || []);
    // Apply stakeholder exclusions from preset when present (e.g. GC preset excludes "General Contractor")
    if (converted.company_stakeholder_types_exclusions != null) {
      setStakeholderExclusions(converted.company_stakeholder_types_exclusions);
    } else {
      setStakeholderExclusions('');
    }
  }, [
    selectedRole,
    data,
    isLoading,
    saveTriggers,
    setStakeholderExclusions,
    setPastProjectStages,
    setExcludePastProjectStages,
  ]);

  const handleRoleToggle = (roleId: string) => {
    // Single select - replace current selection
    if (selectedRole === roleId) {
      onStagesChange([]);
    } else {
      onStagesChange([roleId]);
    }
  };

  const handleCardClick = (roleId: string) => {
    handleRoleToggle(roleId);
  };

  const isPresetLoading = !!presetKey && isLoading;

  return (
    <SectionWrapper>
      <Header
        title="What's your role?"
        description="Pick your role and we'll find your ideal entry point."
      />
      <Box sx={checkboxContainerStyle}>
        {ROLES.map((role) => {
          const isSelected = selectedRole === role.id;
          const showSpinner = isPresetLoading && isSelected;

          return (
            <Box
              key={role.id}
              role="button"
              tabIndex={isPresetLoading ? -1 : 0}
              sx={checkboxItemStyle(isSelected, isPresetLoading)}
              onClick={() => !isPresetLoading && handleCardClick(role.id)}
              onKeyDown={(e) => {
                if (isPresetLoading) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCardClick(role.id);
                }
              }}
              aria-pressed={isSelected}
              aria-busy={showSpinner}
            >
              <FlexRow
                sx={{ gap: 'padding_12', alignItems: 'flex-start', width: '100%' }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    paddingTop: '2px',
                  }}
                >
                  {showSpinner ? (
                    <Spinner size="sm" />
                  ) : (
                    <Checkbox
                      isChecked={isSelected}
                      onToggle={() => handleRoleToggle(role.id)}
                      sx={{ pointerEvents: 'none' }}
                    />
                  )}
                </Box>
                <FlexColumn sx={{ flex: 1 }}>
                  <Text.LargeBold sx={roleTitleStyle}>{role.title}</Text.LargeBold>
                  <Text.Body sx={goalTextStyle}>{role.goalText}</Text.Body>
                </FlexColumn>
              </FlexRow>
            </Box>
          );
        })}
      </Box>
    </SectionWrapper>
  );
};

export default LatestProjectStageStep;
